import { useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Printer } from 'lucide-react';

interface ShopQRCodeProps {
    shopOwnerAddress: string;
    shopName: string;
}

export default function ShopQRCode({ shopOwnerAddress, shopName }: ShopQRCodeProps) {
    const [copied, setCopied] = useState(false);
    const [showPrintView, setShowPrintView] = useState(false);

    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const shopLink = `${appUrl}/?shop=${shopOwnerAddress}`;

    const handleCopy = () => {
        navigator.clipboard.writeText(shopLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handlePrint = () => {
        setShowPrintView(true);
        setTimeout(() => {
            window.print();
            setShowPrintView(false);
        }, 100);
    };

    return (
        <>
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[18px]">
                    Counter QR
                </h2>

                <div className="flex flex-col items-center">
                    {/* QR Code */}
                    <div className="p-4 bg-white rounded-[10px] border-[3px] border-primary">
                        <QRCodeSVG
                            value={shopLink}
                            size={180}
                            level="M"
                            bgColor="#ffffff"
                            fgColor="#111111"
                        />
                    </div>
                    <p className="font-heading text-[8px] text-body-grey mt-4 text-center">
                        SCAN TO ORDER AT {shopName.toUpperCase()}
                    </p>

                    {/* Link display */}
                    <div className="w-full mt-4 p-2.5 bg-background rounded-[8px] border-2 border-hairline">
                        <p className="font-body text-[11px] text-body-grey truncate text-center">
                            {shopLink}
                        </p>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2.5 mt-4 w-full">
                        <button
                            onClick={handleCopy}
                            className="flex-1 rounded-full bg-coral text-white font-grotesk font-bold text-[13px] py-3 flex items-center justify-center gap-2"
                        >
                            <Copy size={13} /> {copied ? 'Copied!' : 'Copy link'}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex-1 rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-3 flex items-center justify-center gap-2 hover:border-coral hover:text-coral"
                        >
                            <Printer size={13} /> Print
                        </button>
                    </div>
                </div>
            </div>

            {/* Print-only overlay. Portaled to <body>: the @media print rule
                hides every DIRECT body child except .print-qr-view, so nested
                inside #root this printed a blank page. */}
            {showPrintView && createPortal(
                <div className="print-qr-view fixed inset-0 z-[200] bg-white flex items-center justify-center">
                    <div className="text-center p-8">
                        <h1 className="text-3xl font-bold text-black mb-2">{shopName}</h1>
                        <p className="text-lg text-gray-600 mb-6">Scan to earn rewards!</p>
                        <div className="inline-block p-6 border-4 border-black">
                            <QRCodeSVG value={shopLink} size={280} level="H" bgColor="#ffffff" fgColor="#000000" />
                        </div>
                        <p className="text-sm text-gray-500 mt-4">Powered by Bit Brew</p>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}
